import { renameKeysFromRESTResponseToSDKResponse } from '@wix/sdk-runtime/rename-all-nested-keys';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { EventDefinition, HttpClient, NonNullablePaths } from '@wix/sdk-types';
import {
  GetSenderDetailsResponse,
  ResolveActualFromAddressResponse,
  SenderDetails,
  SenderDetailsUpdatedEnvelope,
  UpdateSenderDetailsResponse,
  VerifyEmailApplicationErrors,
  getSenderDetails as universalGetSenderDetails,
  resolveActualFromAddress as universalResolveActualFromAddress,
  updateSenderDetails as universalUpdateSenderDetails,
  verifyEmail as universalVerifyEmail,
} from './email-marketing-v1-sender-details-sender-details.universal.js';

export const __metadata = { PACKAGE_NAME: '@wix/email-marketing' };

export function getSenderDetails(
  httpClient: HttpClient
): GetSenderDetailsSignature {
  return () =>
    universalGetSenderDetails(
      // @ts-ignore
      { httpClient }
    );
}

interface GetSenderDetailsSignature {
  /**
   * Retrieves sender details.
   *
   * The sender details include the information displayed as the email sender's name and email address.
   *
   * + If you send an email campaign with a public email domain (e.g.
   * @deprecated
   */
  (): Promise<
    NonNullablePaths<
      GetSenderDetailsResponse,
      `senderDetails.fromName` | `senderDetails.fromEmail`,
      3
    >
  >;
}

export function updateSenderDetails(
  httpClient: HttpClient
): UpdateSenderDetailsSignature {
  return (senderDetails: SenderDetails) =>
    universalUpdateSenderDetails(
      senderDetails,
      // @ts-ignore
      { httpClient }
    );
}

interface UpdateSenderDetailsSignature {
  /**
   * Updates sender details (only provided fields will be updated).
   *
   * If `from_email` is changed - a verification code will be sent to new email address.
   * If verification is needed, a verification email will be sent to the user, and `verification_needed` will return true.
   * @param - New sender details.
   * @deprecated
   */
  (senderDetails: SenderDetails): Promise<
    NonNullablePaths<UpdateSenderDetailsResponse, `verificationNeeded`, 2>
  >;
}

export function verifyEmail(httpClient: HttpClient): VerifyEmailSignature {
  return (verificationCode: string) =>
    universalVerifyEmail(
      verificationCode,
      // @ts-ignore
      { httpClient }
    );
}

interface VerifyEmailSignature {
  /**
   * Verifies the sender's email using a verification code sent to the user's email address upon update.
   * @param - Verification code.
   * @deprecated
   */
  (verificationCode: string): Promise<
    void & {
      __applicationErrorsType?: VerifyEmailApplicationErrors;
    }
  >;
}

export function resolveActualFromAddress(
  httpClient: HttpClient
): ResolveActualFromAddressSignature {
  return (fromAddress: string) =>
    universalResolveActualFromAddress(
      fromAddress,
      // @ts-ignore
      { httpClient }
    );
}

interface ResolveActualFromAddressSignature {
  /**
   * Checks if the sender's email address will be used as from-address or replaced (not related to current sender details).
   * @param - User's provided address from which to send email marketing campaign.
   * @deprecated
   */
  (fromAddress: string): Promise<
    NonNullablePaths<ResolveActualFromAddressResponse, `actualFromAddress`, 2>
  >;
}

export const onSenderDetailsUpdated = EventDefinition(
  'wix.email_marketing.v1.sender_details_updated',
  true,
  (event: SenderDetailsUpdatedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'entity.dateVerified' },
            { path: 'metadata.eventTime' },
          ],
        },
      ])
    )
)<SenderDetailsUpdatedEnvelope>();

export {
  AccountInfo,
  AccountInfoMetadata,
  ActionEvent,
  BaseEventMetadata,
  DomainEvent,
  DomainEventBodyOneOf,
  EntityCreatedEvent,
  EntityDeletedEvent,
  EntityUpdatedEvent,
  EventMetadata,
  GetSenderDetailsRequest,
  GetSenderDetailsResponse,
  IdentificationData,
  IdentificationDataIdOneOf,
  MessageEnvelope,
  ResolveActualFromAddressRequest,
  ResolveActualFromAddressResponse,
  RestoreInfo,
  SenderDetails,
  SenderDetailsUpdatedEnvelope,
  UpdateSenderDetailsRequest,
  UpdateSenderDetailsResponse,
  VerifySenderEmailRequest,
  VerifySenderEmailResponse,
  WebhookIdentityType,
} from './email-marketing-v1-sender-details-sender-details.universal.js';
